import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sum } from 'lib/array/arrayUtils'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'

export type ChatSnapPoint = 'collapsed' | 'expanded'

const NEAR_BOTTOM_THRESHOLD = 32
const OUT_OF_VIEW_THRESHOLD = 116
const SCROLL_FLAG_RESET_DELAY = 500
const SPACER_PADDING = 40
const ADDITIONAL_BUFFER = 50

// Scroll-to-new-message constants (moved from useChatStreaming)
const MAX_SCROLL_RETRIES = 3
const MAX_ELEMENT_FIND_RETRIES = 5
const FALLBACK_SPACER_RATIO = 0.6
const SPACER_RECALCULATE_DELAY = 100
const WRAPPER_VERTICAL_PADDING = 44
const LOADING_INDICATOR_HEIGHT = 64

/**
 * localStorage key for persisting last-read message IDs per chat.
 */
const UNREAD_STORAGE_KEY = 'tripPlanner.chatLastReadMessage'

function loadUnreadMap(): Map<string, string | null> {
  if (typeof window === 'undefined') {
    return new Map()
  }
  try {
    const stored = window.localStorage.getItem(UNREAD_STORAGE_KEY)
    if (!stored) {
      return new Map()
    }
    const parsed = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null) {
      return new Map()
    }
    return new Map(Object.entries(parsed))
  } catch {
    return new Map()
  }
}

let saveDebounceTimer: ReturnType<typeof setTimeout> | undefined = undefined

function saveUnreadMap(map: Map<string, string | null>): void {
  if (typeof window === 'undefined') {
    return
  }

  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer)
  }
  saveDebounceTimer = setTimeout(() => {
    try {
      const obj: Record<string, string | null> = {}
      map.forEach((value, key) => {
        obj[key] = value
      })
      window.localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(obj))
    } catch {}
  }, 500)
}

interface UseChatScrollArgs {
  /** Ref to the scrollable messages container */
  messagesWrapperRef: React.MutableRefObject<HTMLDivElement | null>
  /** Whether the assistant is currently streaming a response */
  isStreaming: boolean
  /** Whether the current stream is a regeneration (no new user message) */
  isRegenerating: boolean
  /** Array of messages for unread anchor logic */
  messages: Array<MessageItem>
  /** Unique key per chat for per-chat unread state tracking */
  chatKey: string
  /** Current mobile overlay snap point */
  chatSnapPoint: ChatSnapPoint
  /** Whether the mobile overlay experience is active */
  shouldUseMobileOverlay: boolean
  /** Whether user is viewing history (disables follow and hides button) */
  isViewingHistory: boolean
  /** Whether there are any messages in the chat */
  hasMessages: boolean
  /** Index of active history entry, if viewing history */
  activeHistoryIndex: number | undefined
}

interface UseChatScrollReturn {
  /** Unified scroll handler for MessagesWrapper.onScroll */
  handleScroll: () => void
  /** Scroll to bottom of the messages container (always uses smooth animation) */
  scrollToBottom: (force?: boolean) => void
  /** Whether the user is currently near the bottom of the scroll container */
  isAtBottom: boolean
  /** Whether new streaming content should auto-follow (scroll to bottom) */
  followTail: boolean
  /** Whether there is content below the current viewport */
  hasContentOutOfView: boolean
  /** Enable follow mode and immediately scroll to bottom (for button click) */
  enableFollowAndScrollToBottom: () => void
  /** Setter for followTail, exposed for external control (e.g., resetConversationState) */
  setFollowTail: React.Dispatch<React.SetStateAction<boolean>>
  scrollToMessage: (messageId: string | undefined) => boolean
  calculateSpacerHeight: () => number
  dynamicSpacerHeight: number
  setDynamicSpacerHeight: React.Dispatch<React.SetStateAction<number>>
  /** Scroll to a newly submitted user message with spacer calculation and retry logic */
  scrollToNewMessage: (messageId: string) => void
  /** Recalculate spacer height after stream ends (delayed for layout settle) */
  recalculateSpacerAfterStream: () => void
}

export function useChatScroll(args: UseChatScrollArgs): UseChatScrollReturn {
  const {
    messagesWrapperRef,
    isStreaming,
    isRegenerating,
    messages,
    chatKey,
    chatSnapPoint,
    shouldUseMobileOverlay,
    isViewingHistory,
    hasMessages,
    activeHistoryIndex,
  } = args

  // ─────────────────────────────────────────────────────────────────────────────
  // Core scroll state
  // ─────────────────────────────────────────────────────────────────────────────

  const [isAtBottom, setIsAtBottom] = useState<boolean>(true)
  const [followTail, setFollowTail] = useState<boolean>(false)
  const [hasContentOutOfView, setHasContentOutOfView] = useState<boolean>(false)
  const [dynamicSpacerHeight, setDynamicSpacerHeight] = useState<number>(0)

  // Track last scroll position to detect scroll direction
  const lastScrollTopRef = useRef<number>(0)
  // Track if a programmatic smooth scroll is in progress
  const isSmoothScrollingRef = useRef<boolean>(false)
  // Track if a programmatic scroll is in progress (to ignore scroll events for followTail changes)
  const isProgrammaticScrollRef = useRef<boolean>(false)
  const lastReadMessageByChatRef = useRef<Map<string, string | null>>(loadUnreadMap())
  const previousChatSnapPointRef = useRef<ChatSnapPoint>(chatSnapPoint)
  const isCollapsedMobile = shouldUseMobileOverlay && chatSnapPoint === 'collapsed'

  /**
   * Cancel any ongoing smooth scroll by doing an instant scroll to current position.
   */
  const cancelSmoothScroll = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (wrapper && isSmoothScrollingRef.current) {
      wrapper.scrollTo({
        top: wrapper.scrollTop,
        behavior: 'auto',
      })
      isSmoothScrollingRef.current = false
    }
  }, [messagesWrapperRef])

  /**
   * Scroll the messages container to the bottom with smooth animation.
   * @param force - If true, scrolls even if followTail is false
   */
  const scrollToBottom = useCallback((force = false) => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }
    if (force || followTail) {
      const targetScrollTop = wrapper.scrollHeight - wrapper.clientHeight
      isProgrammaticScrollRef.current = true
      isSmoothScrollingRef.current = true
      wrapper.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      })

      setTimeout(() => {
        isSmoothScrollingRef.current = false
        isProgrammaticScrollRef.current = false
      }, 500)
    }
  }, [followTail, messagesWrapperRef])
  const checkIsNearBottom = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    const actualScrollHeight = wrapper.scrollHeight - dynamicSpacerHeight
    const scrollOffset = actualScrollHeight - (wrapper.scrollTop + wrapper.clientHeight)
    return scrollOffset <= NEAR_BOTTOM_THRESHOLD
  }, [messagesWrapperRef, dynamicSpacerHeight])
  const checkHasContentOutOfView = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    const actualScrollHeight = wrapper.scrollHeight - dynamicSpacerHeight
    const hasScrollableContent = actualScrollHeight > wrapper.clientHeight
    if (!hasScrollableContent) {
      return false
    }
    const scrollOffset = actualScrollHeight - (wrapper.scrollTop + wrapper.clientHeight)
    return scrollOffset > OUT_OF_VIEW_THRESHOLD
  }, [messagesWrapperRef, dynamicSpacerHeight])

  const getLastReadMessageId = useCallback(() => (
    lastReadMessageByChatRef.current.get(chatKey) ?? null
  ), [chatKey])

  const setLastReadMessageId = useCallback((messageId: string | null) => {
    lastReadMessageByChatRef.current.set(chatKey, messageId)
    saveUnreadMap(lastReadMessageByChatRef.current)
  }, [chatKey])

  const markLatestMessageAsRead = useCallback(() => {
    const latestMessageId = messages[messages.length - 1]?.id
    if (latestMessageId) {
      setLastReadMessageId(latestMessageId)
    }
  }, [messages, setLastReadMessageId])

  const scrollToMessage = useCallback((messageId: string | undefined) => {
    if (!messageId) {
      return false
    }
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    const target = wrapper.querySelector<HTMLElement>(`[data-message-id='${messageId}']`)
    if (!target) {
      return false
    }

    const scrollTarget = target.offsetTop
    isProgrammaticScrollRef.current = true
    isSmoothScrollingRef.current = true
    setIsAtBottom(false)
    wrapper.scrollTo({
      top: Math.max(0, scrollTarget),
      behavior: 'smooth',
    })
    setTimeout(() => {
      isProgrammaticScrollRef.current = false
      isSmoothScrollingRef.current = false
    }, SCROLL_FLAG_RESET_DELAY)
    return true
  }, [messagesWrapperRef])
  const calculateSpacerHeight = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return 0

    const viewportHeight = wrapper.clientHeight
    const allMessages = wrapper.querySelectorAll<HTMLElement>('[data-message-id]')
    const lastTwoMessages = Array.from(allMessages).slice(-2)

    const contentHeight = sum(lastTwoMessages, (message) => message.offsetHeight)
    const toolbar = wrapper.querySelector<HTMLElement>('[data-assistant-toolbar]')
    const toolbarHeight = toolbar?.offsetHeight || 0

    const remainingSpace = viewportHeight - contentHeight - SPACER_PADDING - toolbarHeight - ADDITIONAL_BUFFER

    return remainingSpace > 0 ? remainingSpace : 0
  }, [messagesWrapperRef])

  const resolveUnreadAnchorId = useCallback(() => {
    const latestMessageId = messages[messages.length - 1]?.id
    if (!latestMessageId) {
      return { hasUnread: false, targetId: null, latestMessageId: null }
    }

    const lastReadMessageId = getLastReadMessageId()
    const hasUnread = !lastReadMessageId || lastReadMessageId !== latestMessageId

    if (!hasUnread) {
      return { hasUnread: false, targetId: null, latestMessageId }
    }

    const lastReadIndex = lastReadMessageId
      ? messages.findIndex(message => message.id === lastReadMessageId)
      : -1
    const lastUnreadMessage = lastReadIndex >= 0
      ? messages.slice(lastReadIndex + 1).pop()
      : messages[messages.length - 1]

    if (!lastUnreadMessage) {
      return { hasUnread: false, targetId: null, latestMessageId }
    }

    const lastUnreadIndex = messages.findIndex(message => message.id === lastUnreadMessage.id)
    if (lastUnreadIndex <= 0) {
      return { hasUnread, targetId: lastUnreadMessage.id, latestMessageId }
    }

    const previousUserMessage = [...messages]
      .slice(0, lastUnreadIndex)
      .reverse()
      .find(message => message.role === 'user')

    return {
      hasUnread,
      targetId: previousUserMessage?.id ?? lastUnreadMessage.id,
      latestMessageId,
    }
  }, [getLastReadMessageId, messages])

  const handleScroll = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }

    const currentScrollTop = wrapper.scrollTop
    const previousScrollTop = lastScrollTopRef.current
    const isScrollingUp = currentScrollTop < previousScrollTop
    const isScrollingDown = currentScrollTop > previousScrollTop
    const isProgrammaticScroll = isProgrammaticScrollRef.current

    if (isStreaming && isScrollingDown && !isProgrammaticScroll && !isSmoothScrollingRef.current) {
      const viewportHeight = wrapper.clientHeight
      const spacer = wrapper.querySelector<HTMLElement>('[data-spacer]')
      const spacerHeight = spacer?.offsetHeight || 0
      const contentHeight = wrapper.scrollHeight - spacerHeight

      if (contentHeight <= viewportHeight) {
        wrapper.scrollTop = previousScrollTop
        return
      }
    }

    if (isScrollingUp && isSmoothScrollingRef.current && !isProgrammaticScroll) {
      cancelSmoothScroll()
    }

    lastScrollTopRef.current = currentScrollTop

    const nearBottom = checkIsNearBottom()
    const contentOutOfView = checkHasContentOutOfView()

    setIsAtBottom(nearBottom)

    if (!isProgrammaticScroll) {
      if (isScrollingUp) {
        setFollowTail(false)
      } else if (isScrollingDown && nearBottom && isStreaming) {
        setFollowTail(true)
      }
    }

    if (isViewingHistory || isCollapsedMobile || isSmoothScrollingRef.current) {
      setHasContentOutOfView(false)
    } else {
      setHasContentOutOfView(contentOutOfView)
    }

    if (nearBottom) {
      markLatestMessageAsRead()
    }
  }, [messagesWrapperRef, cancelSmoothScroll, checkIsNearBottom, checkHasContentOutOfView, isStreaming, isViewingHistory, isCollapsedMobile, markLatestMessageAsRead])

  const enableFollowAndScrollToBottom = useCallback(() => {
    setFollowTail(true)
    setIsAtBottom(true)
    setHasContentOutOfView(false)
    setDynamicSpacerHeight(0)

    const wrapper = messagesWrapperRef.current
    if (wrapper) {
      isSmoothScrollingRef.current = true
      const actualContentBottom = wrapper.scrollHeight - dynamicSpacerHeight - wrapper.clientHeight
      wrapper.scrollTo({
        top: Math.max(0, actualContentBottom),
        behavior: 'smooth',
      })
      // Clear the flag after animation would typically complete
      setTimeout(() => {
        isSmoothScrollingRef.current = false
      }, 500)
    }
  }, [messagesWrapperRef, dynamicSpacerHeight])

  useEffect(() => {
    if (!hasMessages) {
      return
    }
    scrollToBottom(true)
  }, [activeHistoryIndex, hasMessages, scrollToBottom])

  useEffect(() => {
    if (!shouldUseMobileOverlay) {
      previousChatSnapPointRef.current = chatSnapPoint
      return
    }
    const previousSnapPoint = previousChatSnapPointRef.current
    if (chatSnapPoint === 'expanded' && previousSnapPoint !== 'expanded') {
      const { hasUnread, targetId, latestMessageId } = resolveUnreadAnchorId()
      const tryScrollToTarget = (attempt: number) => {
        const didScroll = (() => {
          if (!shouldUseMobileOverlay) {
            return true
          }
          if (hasUnread && targetId) {
            return scrollToMessage(targetId)
          }
          const wrapper = messagesWrapperRef.current
          if (wrapper) {
            isProgrammaticScrollRef.current = true
            wrapper.scrollTo({
              top: wrapper.scrollHeight - wrapper.clientHeight,
              behavior: 'smooth',
            })
            setTimeout(() => {
              isProgrammaticScrollRef.current = false
            }, 500)
          }
          return true
        })()

        if (didScroll || attempt >= 5) {
          if (latestMessageId) {
            setLastReadMessageId(latestMessageId)
          }
          return
        }
        requestAnimationFrame(() => tryScrollToTarget(attempt + 1))
      }

      requestAnimationFrame(() => tryScrollToTarget(0))
    }
    previousChatSnapPointRef.current = chatSnapPoint
  }, [
    chatSnapPoint,
    resolveUnreadAnchorId,
    scrollToMessage,
    setLastReadMessageId,
    shouldUseMobileOverlay,
    messagesWrapperRef,
  ])

  useEffect(() => {
    if (!shouldUseMobileOverlay || chatSnapPoint !== 'expanded') {
      return
    }
    if (checkIsNearBottom()) {
      markLatestMessageAsRead()
    }
  }, [chatSnapPoint, checkIsNearBottom, markLatestMessageAsRead, messages, shouldUseMobileOverlay])

  useEffect(() => {
    if (isViewingHistory || isCollapsedMobile || isSmoothScrollingRef.current) {
      setHasContentOutOfView(false)
      return
    }
    requestAnimationFrame(() => {
      if (!isSmoothScrollingRef.current) {
        setHasContentOutOfView(checkHasContentOutOfView())
        setIsAtBottom(checkIsNearBottom())
      }
    })
  }, [messages, isStreaming, isViewingHistory, isCollapsedMobile, checkHasContentOutOfView, checkIsNearBottom])

  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }
    const handleNativeScroll = () => {
      if (isViewingHistory || isCollapsedMobile || isSmoothScrollingRef.current) {
        setHasContentOutOfView(false)
        return
      }
      setHasContentOutOfView(checkHasContentOutOfView())
    }
    wrapper.addEventListener('scroll', handleNativeScroll, { passive: true })
    return () => {
      wrapper.removeEventListener('scroll', handleNativeScroll)
    }
  }, [messagesWrapperRef, isViewingHistory, isCollapsedMobile, checkHasContentOutOfView])

  // ─────────────────────────────────────────────────────────────────────────────
  // Scroll-to-new-message (moved from useChatStreaming)
  // ─────────────────────────────────────────────────────────────────────────────

  const initialSpacerHeightRef = useRef<number>(0)
  const hasAdjustedForLoaderRef = useRef<boolean>(false)

  // Adjust spacer for loading indicator during streaming
  useEffect(() => {
    if (isRegenerating) return
    if (isStreaming && !hasAdjustedForLoaderRef.current && initialSpacerHeightRef.current > 0) {
      const newSpacerHeight = initialSpacerHeightRef.current + LOADING_INDICATOR_HEIGHT
      setDynamicSpacerHeight(newSpacerHeight)
      hasAdjustedForLoaderRef.current = true
    }

    if (!isStreaming) {
      hasAdjustedForLoaderRef.current = false
    }
  }, [isStreaming, isRegenerating, setDynamicSpacerHeight])

  // Recalculate spacer as streaming content grows
  useEffect(() => {
    if (isRegenerating) return
    if (!isStreaming || !messagesWrapperRef.current) return

    const updateSpacer = () => {
      const wrapper = messagesWrapperRef.current
      if (!wrapper) return

      const viewportHeight = wrapper.clientHeight

      const lastAssistantMessage = messages.findLast(m => m.role === 'assistant')
      const lastUserMessage = messages.findLast(m => m.role === 'user')

      if (!lastAssistantMessage || !lastUserMessage) return

      const lastAiMessageEl = wrapper.querySelector<HTMLElement>(`[data-message-id="${lastAssistantMessage.id}"]`)
      const lastUserMessageEl = wrapper.querySelector<HTMLElement>(`[data-message-id="${lastUserMessage.id}"]`)

      const aiResponseHeight = lastAiMessageEl?.offsetHeight || 0
      const userMessageHeight = lastUserMessageEl?.offsetHeight || 0

      const remainingSpace = viewportHeight - userMessageHeight - aiResponseHeight
      const newSpacerHeight = Math.max(0, remainingSpace)

      setDynamicSpacerHeight(newSpacerHeight)
    }

    const rafId = requestAnimationFrame(updateSpacer)

    return () => cancelAnimationFrame(rafId)
  }, [isStreaming, isRegenerating, messages, messagesWrapperRef, setDynamicSpacerHeight])

  const scrollToNewMessage = useCallback((messageId: string) => {
    const attemptScroll = (retryCount = 0) => {
      const wrapper = messagesWrapperRef.current
      const userMessageEl = wrapper?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`)

      if (wrapper && userMessageEl) {
        const viewportHeight = wrapper.clientHeight
        const userMessageHeight = userMessageEl.offsetHeight
        const RESERVED_SPACE = WRAPPER_VERTICAL_PADDING + LOADING_INDICATOR_HEIGHT
        const spacerHeight = Math.max(0, viewportHeight - userMessageHeight - RESERVED_SPACE)
        initialSpacerHeightRef.current = spacerHeight
        hasAdjustedForLoaderRef.current = false

        setDynamicSpacerHeight(spacerHeight)

        setTimeout(() => {
          requestAnimationFrame(() => {
            const scrolled = scrollToMessage(messageId)
            if (!scrolled && retryCount < MAX_SCROLL_RETRIES) {
              requestAnimationFrame(() => attemptScroll(retryCount + 1))
            }
          })
        }, 50)
      } else if (retryCount < MAX_ELEMENT_FIND_RETRIES) {
        requestAnimationFrame(() => attemptScroll(retryCount + 1))
      } else {
        setDynamicSpacerHeight(window.innerHeight * FALLBACK_SPACER_RATIO)
        scrollToMessage(messageId)
      }
    }

    // Double RAF to ensure React has committed and painted
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        attemptScroll()
      })
    })
  }, [messagesWrapperRef, scrollToMessage, setDynamicSpacerHeight])

  const recalculateSpacerAfterStream = useCallback(() => {
    setTimeout(() => {
      const height = calculateSpacerHeight()
      setDynamicSpacerHeight(height)
    }, SPACER_RECALCULATE_DELAY)
  }, [calculateSpacerHeight, setDynamicSpacerHeight])

  // Recalculate spacer when the messages wrapper resizes (e.g. browser window resize,
  // panel drag). Without this, the spacer can be stale after a resize, causing layout shift.
  // Debounced to avoid running DOM queries at ~60fps during drag.
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper || typeof ResizeObserver === 'undefined') return

    let debounceTimer: ReturnType<typeof setTimeout> | undefined = undefined
    const observer = new ResizeObserver(() => {
      if (debounceTimer !== undefined) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined
        const height = calculateSpacerHeight()
        setDynamicSpacerHeight(height)
      }, SPACER_RECALCULATE_DELAY)
    })
    observer.observe(wrapper)
    return () => {
      observer.disconnect()
      if (debounceTimer !== undefined) clearTimeout(debounceTimer)
    }
  }, [messagesWrapperRef, calculateSpacerHeight, setDynamicSpacerHeight])

  return useMemo(() => ({
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    scrollToMessage,
    calculateSpacerHeight,
    dynamicSpacerHeight,
    setDynamicSpacerHeight,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
  }), [
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    scrollToMessage,
    calculateSpacerHeight,
    dynamicSpacerHeight,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
  ])
}
